import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Menu, ShieldCheck, Eye, Coins, MoreVertical } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import WorkspaceChatPanel from '../components/WorkspaceChatPanel';
import WorkspaceDrawer, { DrawerSection } from '../components/WorkspaceDrawer';
import PublishStatusBar from '../components/PublishStatusBar';
import { resolveWorkspaceKind } from '../config/workspaceKinds';
import { useT } from '../contexts/LocaleContext';
import { useCredits } from '../contexts/CreditsContext';
import { AdminAvatarMenu } from '../components/shell/AdminAvatarMenu';

interface Workspace {
  id: string;
  name: string;
  description?: string;
  status: string;
  config: any;
  created_at: string;
  project_stack?: string | null;
  project_kind?: string | null;
  project_root?: string | null;
}

/**
 * WorkspaceDetail — full-screen conversational surface (Feb 2026, v2).
 *
 * ChatGPT / WhatsApp / iMessage-style layout: the chat takes the ENTIRE
 * viewport (minus a slim floating top bar). No page paddings, no cards.
 * The conversation is the app.
 *
 * Everything else — files, deploy, memory, config, observability — is
 * one tap away in the right-side drawer, opened by the hamburger button
 * on the header.
 */
const WorkspaceDetail = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const t = useT();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [me, setMe] = useState<{ role?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSection, setDrawerSection] = useState<DrawerSection>('files');
  const [snapshot, setSnapshot] = useState<{ files: number; durable: boolean } | null>(null);

  const loadWorkspace = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const data = await apiService.getWorkspace(workspaceId);
      setWorkspace(data);
    } catch (error) {
      toast.error(t('workspace.loadError'));
      navigate('/home');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, navigate]);

  const loadSnapshotStatus = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const status = await apiService.getWorkspaceSnapshotStatus(workspaceId);
      setSnapshot(status);
    } catch { /* silent */ }
  }, [workspaceId]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);
  useEffect(() => { apiService.getMe().then(setMe).catch(() => setMe(null)); }, []);
  useEffect(() => {
    loadSnapshotStatus();
    const t = setInterval(loadSnapshotStatus, 20000);
    return () => clearInterval(t);
  }, [loadSnapshotStatus]);

  const openDrawer = (section: DrawerSection) => {
    setDrawerSection(section);
    setDrawerOpen(true);
  };

  const openPreview = useCallback(() => {
    if (!workspace) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const token = typeof window !== 'undefined'
      ? (window.localStorage.getItem('nexus_token') || '')
      : '';
    // v24.2 · Cache-busting `t=` so the browser NEVER serves a stale
    // copy when the user clicks Preview twice.
    const parts: string[] = [];
    if (token) parts.push(`token=${encodeURIComponent(token)}`);
    parts.push(`t=${Date.now()}`);
    const url = `${base}/api/workspaces/${workspace.id}/runtime/proxy/?${parts.join('&')}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [workspace]);

  const workspaceKind = useMemo(
    () => resolveWorkspaceKind(workspace?.project_stack),
    [workspace?.project_stack],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" style={{ width: '40px', height: '40px' }} />
      </div>
    );
  }
  if (!workspace) return null;

  return (
    <div
      className="workspace-shell content-with-nav-aware"
      style={{ color: 'hsl(var(--text))' }}
      data-testid="workspace-page"
    >
      {/* Slim floating header — 56-64px tall */}
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="workspace-header"
      >
        <div className="workspace-header-inner">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/home')}
              data-testid="workspace-back-button"
              className="w-9 h-9 grid place-items-center rounded-full transition shrink-0"
              style={{
                color: 'hsl(var(--text-muted))',
                border: '1px solid hsl(var(--border))',
                background: 'hsl(var(--bg-elev) / 0.6)',
              }}
              aria-label={t('workspace.backHome')}
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <h1
                className="text-sm sm:text-base font-medium tracking-tight truncate"
                style={{ color: 'hsl(var(--text))' }}
                data-testid="workspace-name"
              >
                {workspace.name}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider"
                  style={{ color: 'hsl(var(--text-muted))' }}
                  data-testid="workspace-id-chip"
                >
                  ID: {workspace.id.slice(0, 8)}
                </span>
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-medium"
                  style={{ color: workspace.status === 'active' ? 'hsl(155 65% 55%)' : 'hsl(var(--text-muted))' }}
                  data-testid="workspace-status-chip"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: workspace.status === 'active' ? 'hsl(155 65% 55%)' : 'hsl(var(--text-muted))' }}
                  />
                  {workspace.status === 'active' ? 'Activo' :
                   workspace.status === 'temp' ? 'Nuevo' : workspace.status}
                </span>
                {workspace.project_stack && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider"
                    style={{
                      background: `${workspaceKind.accent.replace(')', ' / 0.14)')}`,
                      border: `1px solid ${workspaceKind.accent.replace(')', ' / 0.35)')}`,
                      color: workspaceKind.accent,
                    }}
                    data-testid="workspace-kind-badge"
                    title={workspaceKind.tagline}
                  >
                    {workspaceKind.label}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {snapshot && snapshot.files > 0 && (
              <div
                className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono"
                style={{
                  background: 'hsl(var(--accent) / 0.12)',
                  border: '1px solid hsl(var(--accent) / 0.28)',
                  color: 'hsl(var(--accent))',
                }}
                data-testid="workspace-snapshot-pill"
                title={`${snapshot.files} ${t('workspace.durableFiles')}`}
              >
                <ShieldCheck className="w-3 h-3" />
                {snapshot.files} · {t('workspace.durable')}
              </div>
            )}

            {/* Credits chip — pulses cyan when the balance changes */}
            <CreditsChipInHeader />

            {/* PRIMARY ACTIONS — always visible on desktop */}
            <button
              type="button"
              onClick={openPreview}
              data-testid="workspace-preview-btn"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition"
              style={{
                background: 'hsl(var(--bg-elev) / 0.6)',
                border: '1px solid hsl(var(--border))',
                color: 'hsl(var(--text))',
              }}
              title={t('workspace.previewHint')}
            >
              <Eye className="w-3.5 h-3.5" />
              <span>{t('workspace.preview')}</span>
            </button>

            <PublishStatusBar
              workspaceId={workspace.id}
              onShowHistory={() => openDrawer('deploy')}
            />

            <button
              type="button"
              onClick={() => openDrawer('files')}
              data-testid="workspace-open-drawer"
              className="w-9 h-9 grid place-items-center rounded-full transition"
              style={{
                color: 'hsl(var(--text))',
                border: '1px solid hsl(var(--border))',
                background: 'hsl(var(--bg-elev) / 0.6)',
              }}
              aria-label={t('workspace.moreActions')}
              title={t('workspace.moreActions')}
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {/* Avatar menu — profile, settings, admin hubs */}
            <div className="ml-1">
              <AdminAvatarMenu isAdmin={(me?.role === 'admin' || me?.role === 'owner')} />
            </div>
          </div>
        </div>
      </motion.header>

      {/* Chat — occupies the entire remaining viewport */}
      <div className="workspace-chat-container">
        <WorkspaceChatPanel
          workspaceId={workspace.id}
          workspaceName={workspace.name}
          workspaceKind={workspaceKind}
          onOpenDrawer={openDrawer}
        />
      </div>

      <WorkspaceDrawer
        open={drawerOpen}
        section={drawerSection}
        onSectionChange={setDrawerSection}
        onClose={() => setDrawerOpen(false)}
        workspace={workspace}
        onWorkspaceUpdate={loadWorkspace}
      />
    </div>
  );
};

export default WorkspaceDetail;

// ── Credits chip used in the top bar. Clicking it opens Finanzas.
const CreditsChipInHeader: React.FC = () => {
  const nav = useNavigate();
  const { balance } = useCredits();
  const tr = useT();
  const [pulse, setPulse] = useState(false);
  const prev = React.useRef<number | null>(null);
  useEffect(() => {
    if (balance !== null && prev.current !== null && balance !== prev.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 900);
      prev.current = balance;
      return () => clearTimeout(t);
    }
    prev.current = balance;
  }, [balance]);
  const display = balance == null ? '—' : Math.floor(balance).toString();
  const isNegative = typeof balance === 'number' && balance < 0;
  return (
    <button
      type="button"
      onClick={() => nav('/creditos')}
      data-testid="workspace-credits-chip"
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition"
      style={{
        background: isNegative ? 'hsl(0 84% 65% / 0.12)' : 'hsl(var(--accent) / 0.12)',
        border: `1px solid ${isNegative ? 'hsl(0 84% 65% / 0.6)' : 'hsl(var(--accent) / ' + (pulse ? 0.7 : 0.32) + ')'}`,
        color: isNegative ? 'hsl(0 84% 65%)' : 'hsl(var(--accent))',
        boxShadow: pulse ? '0 0 18px hsl(var(--accent) / 0.5)' : 'none',
      }}
      title={tr('credits.balance')}
      aria-label={tr('credits.balance')}
    >
      <Coins className="w-3.5 h-3.5" />
      <span>{tr('credits.label')} {display}</span>
    </button>
  );
};
